import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import FraudRecords from './components/FraudRecords/FraudRecords';
import NewFraudRecord from './components/FraudRecords/NewFraudRecord';
import FraudRecordDetail from './components/FraudRecords/FraudRecordDetail';
// import FightersBoard from './components/FightersBoard/FightersBoard';
import Admin from './components/Admin/Admin';
import Profile from './components/Profile/Profile';
import Settings from './components/Settings/Settings';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #181a1f 0%, #1e2025 50%, #24252a 100%)'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Admin Route Component (only for admin users)
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #181a1f 0%, #1e2025 50%, #24252a 100%)'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/fraud-records" />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #181a1f 0%, #1e2025 50%, #24252a 100%)'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/fraud-records" /> : children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />

      {/* Protected Routes */}
      <Route path="/fraud-records" element={
        <ProtectedRoute>
          <Layout>
            <FraudRecords />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/fraud-records/new" element={
        <ProtectedRoute>
          <Layout>
            <NewFraudRecord />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/fraud-records/:id" element={
        <ProtectedRoute>
          <Layout>
            <FraudRecordDetail />
          </Layout>
        </ProtectedRoute>
      } />
      {/* <Route path="/fighters-board" element={
        <ProtectedRoute>
          <Layout>
            <FightersBoard />
          </Layout>
        </ProtectedRoute>
      } /> */}
      <Route path="/admin" element={
        <AdminRoute>
          <Layout>
            <Admin />
          </Layout>
        </AdminRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <Profile />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <Settings />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/fraud-records" replace />} />
      <Route path="*" element={<Navigate to="/fraud-records" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
